import type { Strength } from "@/lib/paraphrase";

/** What each strength does, in the words a person would search for. Built on the same hand-checked rules (no AI). */
export const MODE_INFO: Record<Strength, { label: string; hint: string }> = {
  light: { label: "Light", hint: "Light: swaps whole phrases only, such as \"in order to\" to \"to\". The smallest, safest edits." },
  medium: { label: "Medium", hint: "Medium: phrase swaps plus a short list of safe single words, such as big to large and buy to purchase." },
  strong: { label: "Strong", hint: "Strong: everything in Medium, plus connectors, contractions, and moving a \"because\" clause to the front of the sentence." },
};

/** The strength and the exact text a shown result was made from. */
export interface ShownResult { strength: Strength; input: string }

/**
 * True when the result on screen was made with another strength or from other text than what is selected now
 * (B-0154). It is marked out of date until Paraphrase is pressed again, so a Strong result never looks like the
 * result of Light.
 */
export function resultIsStale(shown: ShownResult | null, strength: Strength, input: string): boolean {
  return !!shown && (shown.strength !== strength || shown.input !== input);
}

export function staleMessage(shown: ShownResult, strength: Strength, input: string): string {
  const parts: string[] = [];
  if (shown.strength !== strength) parts.push(`This result is from ${MODE_INFO[shown.strength].label}, but ${MODE_INFO[strength].label} is selected`);
  if (shown.input !== input) parts.push(parts.length ? "your text has changed too" : "Your text has changed since this result");
  return `${parts.join(" and ")}. Press Paraphrase to update it.`;
}

/** The note for a run that found nothing to change: says so, names the stronger setting to try, and that no trial use was counted. */
export function noChangesMessage(strength: Strength): string {
  const next =
    strength === "light" ? "Light only swaps whole phrases, so many sentences stay as they are. Try Medium or Strong."
    : strength === "medium" ? "Try Strong, which also changes connectors and moves clauses."
    : "Try a longer passage, or edit the wording by hand.";
  return `No safe changes found for this text at ${MODE_INFO[strength].label}, so no trial use was counted. ${next}`;
}
